// Controlled bilingual content — no runtime machine translation.
// All editorial copy is factual: no invented customers, partners, figures or awards.

export const content = {
  fr: {
    nav: {
      home: "Accueil",
      about: "À propos",
      innovation: "Innovation",
      applications: "Applications",
      mobility: "Mobilité internationale",
      services: "Services",
      contact: "Contact",
      cta: "Découvrir Learnova",
      menuOpen: "Ouvrir le menu",
      menuClose: "Fermer le menu",
      langLabel: "Choisir la langue",
      skip: "Aller au contenu principal",
    },
    common: {
      status: {
        prototype: "Prototype fonctionnel",
        development: "En développement",
        concept: "Concept / Feuille de route",
      },
      learnMore: "En savoir plus",
      externalNewTab: "ouvre un nouvel onglet",
    },
    home: {
      seo: {
        title: "Congo Bourse — Innovation & Technologie",
        description:
          "Congo Bourse relie les personnes, les opportunités et l'innovation. Découvrez MouyIA, son initiative Innovation & Technologie, et Learnova, prototype fonctionnel d'intelligence d'apprentissage.",
      },
      hero: {
        eyebrow: "Congo Bourse — Innovation & Technologie",
        lines: ["Relier les personnes,", "les opportunités", "et l'innovation."],
        sub: "Congo Bourse conjugue une expérience de la mobilité internationale avec un engagement croissant dans l'innovation numérique et les solutions technologiques, au service des parcours éducatifs et professionnels.",
        ctaPrimary: "Explorer nos innovations",
        ctaSecondary: "Découvrir Learnova",
        frameAlt: "Professionnels collaborant autour de technologies numériques, d'IA et d'outils d'apprentissage",
        frameCaption: "Du terrain au numérique — une même exigence.",
        badgeTitle: "Learnova",
        badgeSub: "Prototype fonctionnel — démonstration publique",
      },
      marquee: [
        "Congo Bourse",
        "MouyIA — Innovation & Technologie",
        "Learnova — Prototype fonctionnel",
        "De l'objectif à la compétence démontrable",
        "Mobilité internationale",
        "Solutions numériques",
      ],
      evolution: {
        eyebrow: "Notre trajectoire",
        title: "Une évolution cohérente",
        intro:
          "Congo Bourse a construit son parcours étape par étape : de l'accompagnement à la mobilité internationale vers la conception de solutions numériques.",
        steps: [
          {
            title: "Mobilité internationale",
            text: "Accompagner les parcours éducatifs et professionnels au-delà des frontières.",
          },
          {
            title: "Transformation numérique",
            text: "Intégrer des outils numériques et des services technologiques à nos pratiques.",
          },
          {
            title: "Innovation & Technologie",
            text: "Concevoir des solutions répondant à des problèmes concrets : éducation, compétences, mobilité.",
          },
          {
            title: "MouyIA",
            text: "L'initiative Innovation & Technologie de Congo Bourse, à travers laquelle nos solutions numériques sont développées et présentées.",
          },
          {
            title: "Applications numériques",
            text: "Des applications concrètes, présentées avec leur stade réel de développement.",
          },
        ],
      },
      featured: {
        eyebrow: "Innovation vedette",
        name: "Learnova",
        category: "Système d'intelligence d'apprentissage guidé par les objectifs",
        tagline: "De l'objectif à la compétence démontrable.",
        description:
          "Learnova transforme ce qu'un apprenant veut accomplir en un parcours d'apprentissage structuré, axé sur le développement des compétences — plutôt que de partir d'un catalogue de cours.",
        ctaDiscover: "Découvrir Learnova",
        ctaPrototype: "Voir le prototype fonctionnel",
        principles: [
          "L'objectif avant le contenu",
          "Intelligence des compétences",
          "Apprentissage adaptatif",
          "La preuve avant l'achèvement",
          "Progression ≠ maîtrise",
          "Expérience assistée par IA",
        ],
      },
      portfolio: {
        eyebrow: "Portfolio",
        title: "Nos applications",
        text: "Chaque application est présentée avec son domaine et son stade de maturité réel. Learnova est aujourd'hui notre prototype vedette.",
        cta: "Voir le portfolio",
      },
      transparency: {
        eyebrow: "Transparence",
        title: "Une innovation vérifiable",
        points: [
          {
            title: "Stades réels",
            text: "Chaque solution affiche clairement son statut : prototype fonctionnel, en développement ou concept.",
          },
          {
            title: "Démonstrations publiques",
            text: "Les prototypes et démonstrations accessibles le sont sans compte ni autorisation préalable.",
          },
          {
            title: "Aucune exagération",
            text: "Ce site ne présente ni clients, ni partenaires, ni chiffres qui ne pourraient être vérifiés.",
          },
        ],
      },
      ctaBand: {
        title: "Évaluateurs, partenaires, institutions",
        text: "Vous souhaitez vérifier notre démarche ou échanger avec nous ? Contactez-nous directement.",
        cta: "Nous contacter",
      },
    },
    about: {
      seo: {
        title: "À propos — Congo Bourse",
        description:
          "Le parcours de Congo Bourse : de la mobilité internationale et des opportunités éducatives vers l'innovation numérique et la technologie.",
      },
      eyebrow: "À propos",
      title: "Notre parcours",
      intro:
        "Congo Bourse est une organisation dont les racines se situent dans la mobilité internationale et l'accès aux opportunités éducatives. Son histoire se raconte en quatre chapitres.",
      chapters: [
        {
          num: "01",
          title: "Origines",
          text: "Congo Bourse est née de l'accompagnement vers la mobilité internationale et les opportunités éducatives : aider des personnes à identifier, préparer et saisir des parcours au-delà de leur environnement immédiat.",
        },
        {
          num: "02",
          title: "Évolution",
          text: "Au fil de cette expérience de terrain, des outils numériques et des services technologiques sont venus renforcer nos pratiques — pour mieux informer, mieux accompagner et mieux suivre les parcours.",
        },
        {
          num: "03",
          title: "Innovation",
          text: "Cette évolution a ouvert une nouvelle étape : le développement de solutions numériques répondant à des problèmes validés dans les domaines de l'éducation, des compétences et de la mobilité.",
        },
        {
          num: "04",
          title: "Avenir",
          text: "Notre ambition est un usage responsable de l'intelligence artificielle et des technologies numériques, pour créer des solutions pratiques, pertinentes pour les contextes africains.",
        },
      ],
      imageAlt: "Professionnels collaborant et apprenant autour d'ordinateurs et d'outils numériques",
      identity: {
        title: "Deux identités, une même organisation",
        cb: "Congo Bourse est l'identité institutionnelle : mobilité internationale, services et accompagnement.",
        mouyia:
          "MouyIA est son initiative Innovation & Technologie : l'identité technologique à travers laquelle les solutions numériques sont développées et présentées.",
      },
    },
    innovation: {
      seo: {
        title: "MouyIA — Innovation & Technologie | Congo Bourse",
        description:
          "MouyIA est l'initiative Innovation & Technologie de Congo Bourse : intelligence artificielle, plateformes numériques et technologies d'apprentissage au service de solutions pratiques.",
      },
      eyebrow: "Innovation & Technologie",
      title: "MouyIA",
      subtitle: "L'initiative Innovation & Technologie de Congo Bourse",
      intro:
        "MouyIA est l'initiative à travers laquelle Congo Bourse explore, conçoit et présente ses solutions numériques. Elle ne remplace pas Congo Bourse : elle prolonge sa mission par la technologie.",
      exploreTitle: "Ce que nous explorons",
      explore: [
        { title: "Intelligence artificielle", text: "Des usages ciblés et responsables, au service de la décision humaine." },
        { title: "Plateformes numériques", text: "Des services accessibles, conçus pour des contextes réels." },
        { title: "Automatisation", text: "Simplifier les processus répétitifs pour libérer le temps humain." },
        { title: "Systèmes d'information", text: "Structurer l'information pour mieux décider et mieux accompagner." },
        { title: "Données", text: "Exploiter les données avec rigueur et transparence." },
        { title: "Technologies d'apprentissage", text: "Repenser l'apprentissage autour des compétences démontrables." },
      ],
      methodTitle: "Notre méthode",
      method: [
        { title: "Problème", text: "Partir d'un besoin réel, observé et validé." },
        { title: "Conception", text: "Formaliser une réponse simple et utile." },
        { title: "Prototype", text: "Construire une version fonctionnelle, testable." },
        { title: "Validation", text: "Confronter le prototype à des usages réels." },
        { title: "Amélioration", text: "Itérer avec rigueur, sans survendre." },
      ],
      aiNote:
        "L'intelligence artificielle n'est pas présentée comme un substitut à la décision humaine : elle l'assiste, l'éclaire et la renforce.",
      flagship: {
        eyebrow: "Application vedette",
        text: "Learnova, système d'intelligence d'apprentissage guidé par les objectifs, est le premier prototype fonctionnel issu de cette démarche.",
        cta: "Découvrir Learnova",
        ctaAll: "Voir toutes les applications",
      },
    },
    applications: {
      seo: {
        title: "Applications — Congo Bourse",
        description:
          "Le portfolio d'applications de Congo Bourse et MouyIA : chaque solution est présentée avec son domaine et son stade de maturité réel.",
      },
      eyebrow: "Portfolio",
      title: "Applications",
      intro:
        "Chaque application est publiée avec son domaine et son stade de maturité réel. Aucune application n'est présentée au-delà de son état effectif.",
      legendTitle: "Comment lire les statuts",
      legend: [
        { status: "prototype", text: "L'application fonctionne et peut être démontrée publiquement." },
        { status: "development", text: "L'application est en cours de construction ; certaines fonctions ne sont pas encore disponibles." },
        { status: "concept", text: "L'application est à l'étude ou planifiée ; elle n'est pas encore utilisable." },
      ],
      flagshipLabel: "Application vedette",
      domainLabel: "Domaine",
      domain: "Plateforme d'intelligence d'apprentissage",
      learnovaDesc:
        "Learnova transforme un objectif d'apprentissage en parcours structuré axé sur les compétences. Le prototype fonctionnel est démontrable en français et en anglais.",
      ctaPrototype: "Voir le prototype fonctionnel",
      moreTitle: "Un portfolio évolutif",
      moreText:
        "D'autres applications validées seront publiées ici au fur et à mesure, toujours avec leur stade réel de développement. Aucun produit fictif n'est présenté.",
    },
    learnova: {
      seo: {
        title: "Learnova — Système d'intelligence d'apprentissage guidé par les objectifs | Congo Bourse",
        description:
          "Learnova : de l'objectif à la compétence démontrable. Prototype fonctionnel démontrable publiquement — définition d'objectif, parcours adaptatif, quiz et progression.",
      },
      hero: {
        eyebrow: "Application vedette — MouyIA",
        name: "Learnova",
        category: "Système d'intelligence d'apprentissage guidé par les objectifs",
        tagline: "De l'objectif à la compétence démontrable.",
        description:
          "Learnova transforme ce qu'un apprenant veut accomplir en un parcours d'apprentissage structuré, axé sur le développement des compétences.",
        ctaPrototype: "Voir le prototype fonctionnel",
        ctaDemo: "Regarder la démonstration",
        maturityNote: "Statut actuel : prototype fonctionnel. Ce n'est pas un produit de production.",
      },
      problem: {
        eyebrow: "Le problème",
        title: "Les plateformes partent des cours. Les apprenants partent d'un objectif.",
        text1:
          "Les plateformes d'apprentissage traditionnelles commencent par des catalogues de cours : à l'apprenant de deviner lesquels le mèneront à son but.",
        text2:
          "Or un apprenant commence presque toujours par une question différente : « Qu'est-ce que je veux devenir capable de faire ? »",
      },
      approach: {
        eyebrow: "L'approche Learnova",
        title: "De l'objectif à la maîtrise",
        intro: "Le modèle Learnova enchaîne sept étapes, de l'intention à la compétence démontrable.",
        steps: [
          { label: "Objectif", desc: "Ce que l'apprenant veut devenir capable de faire." },
          { label: "Compétences", desc: "La décomposition de l'objectif en compétences identifiées." },
          { label: "État de l'apprenant", desc: "Le point de départ réel de l'apprenant." },
          { label: "Parcours adaptatif", desc: "Un chemin d'apprentissage structuré à partir de cet état." },
          { label: "Apprentissage", desc: "Des activités d'apprentissage guidées par le parcours." },
          { label: "Preuves", desc: "Des démonstrations de ce qui a réellement été acquis." },
          { label: "Maîtrise", desc: "La compétence démontrable — au-delà de la simple progression." },
        ],
      },
      how: {
        eyebrow: "Le prototype actuel",
        title: "Comment fonctionne le prototype aujourd'hui",
        intro:
          "Voici le parcours réellement implémenté dans le prototype fonctionnel actuel, démontrable en français et en anglais.",
        steps: [
          "Définition de l'objectif",
          "Analyse de l'objectif",
          "Confirmation humaine explicite",
          "Feuille de route d'apprentissage",
          "Tableau de bord",
          "Activité d'apprentissage",
          "Évaluation par quiz",
          "Progression",
        ],
      },
      maturity: {
        eyebrow: "Transparence produit",
        title: "Ce qui existe — et ce qui est en développement",
        nowTitle: "Disponible maintenant",
        now: [
          "Définition de l'objectif",
          "Analyse de l'objectif",
          "Confirmation humaine explicite",
          "Feuille de route d'apprentissage",
          "Tableau de bord",
          "Activité d'apprentissage",
          "Évaluation par quiz",
          "Progression",
          "Règle « l'objectif avant le parcours »",
          "Fondation de domaine initiale",
        ],
        roadmapTitle: "Feuille de route / en développement",
        roadmap: [
          "Moteur de preuves avancé (Evidence Engine)",
          "Moteur de maîtrise (Mastery Engine)",
          "Intelligence adaptative avancée",
          "Capacités institutionnelles multi-utilisateurs",
        ],
        disclaimer:
          "Les éléments de la feuille de route ne sont pas des fonctionnalités actuelles. Ils indiquent la direction de développement du produit.",
      },
      evidence: {
        eyebrow: "Preuves produit",
        title: "Prototype fonctionnel — Captures réelles",
        subtitle: "Captures d'écran réelles du prototype fonctionnel Learnova actuel.",
        steps: [
          {
            title: "Définition de l'objectif",
            caption:
              "L'apprenant définit le résultat d'apprentissage visé, son niveau actuel et le temps d'apprentissage disponible.",
            alt: "Capture réelle du prototype Learnova : écran de définition de l'objectif avec niveau actuel et temps disponible",
          },
          {
            title: "Feuille de route personnalisée",
            caption:
              "Learnova traduit l'objectif de l'apprenant en un itinéraire d'apprentissage structuré et personnalisé.",
            alt: "Capture réelle du prototype Learnova : feuille de route d'apprentissage personnalisée en six étapes",
          },
          {
            title: "Activité d'apprentissage & évaluation",
            caption:
              "L'apprenant suit une activité d'apprentissage et complète un quiz de validation au sein du parcours généré.",
            alt: "Capture réelle du prototype Learnova : module d'apprentissage avec quiz de validation",
          },
          {
            title: "Progression de l'apprenant",
            caption:
              "Le tableau de bord de l'apprenant enregistre la progression dans le parcours d'apprentissage personnalisé.",
            alt: "Capture réelle du prototype Learnova : tableau de bord de progression de l'apprenant",
          },
          {
            title: "Accueil du prototype",
            caption:
              "Le prototype actuel de Learnova présente l'expérience Objectif → IA → Parcours personnalisé.",
            alt: "Capture réelle du prototype Learnova : page d'accueil présentant l'expérience Objectif, IA, Parcours personnalisé",
          },
        ],
        enlarge: "Agrandir la capture",
        close: "Fermer",
        cta: "Explorer le prototype en ligne",
        note: "Le prototype est accessible publiquement et démontre le parcours actuel Objectif → Analyse → Confirmation → Feuille de route → Apprentissage → Quiz → Progression.",
      },
      video: {
        eyebrow: "Démonstration vidéo",
        title: "Voir Learnova en fonctionnement",
        note: "Une démonstration réelle du prototype fonctionnel actuel — présentée en anglais.",
        iframeTitle: "Démonstration vidéo du prototype Learnova",
      },
      live: {
        title: "Ouvrir le prototype fonctionnel",
        text: "Le prototype est accessible publiquement, sans compte, dans un nouvel onglet — sur ordinateur comme sur mobile.",
        cta: "Ouvrir le prototype",
        urlLabel: "Adresse du prototype",
      },
    },
    mobility: {
      seo: {
        title: "Mobilité internationale — Congo Bourse",
        description:
          "Congo Bourse est active dans la mobilité internationale et les services d'accompagnement associés, notamment dans le domaine des opportunités éducatives.",
      },
      eyebrow: "Notre activité historique",
      title: "Mobilité internationale",
      intro:
        "Congo Bourse est active dans la mobilité internationale et les services d'accompagnement associés, notamment dans le domaine des opportunités éducatives. Cette activité constitue le socle de l'organisation et la source de sa compréhension des parcours.",
      imageAlt: "Personne tenant une banderole Congo Bourse « Études & Bourses assurées » dans la rue",
      bridgeTitle: "Le lien avec l'innovation",
      bridge:
        "C'est l'expérience de la mobilité internationale — ses besoins, ses frictions, ses parcours — qui a conduit Congo Bourse vers la transformation numérique puis vers l'innovation technologique portée par MouyIA. Les deux activités sont distinctes : cette page concerne uniquement la mobilité.",
      noteTitle: "Informations détaillées",
      note: "Les informations détaillées sur nos services de mobilité sont en cours de validation et seront publiées sur cette page. Pour toute question, contactez-nous directement.",
      cta: "Nous contacter",
    },
    services: {
      seo: {
        title: "Services — Congo Bourse",
        description:
          "Congo Bourse propose des services liés à la mobilité internationale et à l'accompagnement associé.",
      },
      eyebrow: "Services",
      title: "Nos services",
      intro:
        "Congo Bourse propose des services liés à la mobilité internationale et à l'accompagnement associé, aux côtés de son activité d'innovation technologique.",
      noteTitle: "Catalogue en cours de publication",
      note: "Le détail de nos services est en cours de validation et sera publié ici prochainement. Cette page est structurée pour être enrichie sans refonte. Pour toute demande, contactez-nous directement.",
      cta: "Nous contacter",
    },
    contact: {
      seo: {
        title: "Contact — Congo Bourse",
        description:
          "Contactez Congo Bourse : évaluations, partenariats, mobilité internationale ou questions sur nos innovations.",
      },
      eyebrow: "Contact",
      title: "Écrivez-nous",
      intro:
        "Évaluateurs, partenaires, institutions ou curieux : ce formulaire achemine votre message directement vers notre équipe.",
      form: {
        name: "Nom complet",
        namePlaceholder: "Votre nom et prénom",
        email: "Adresse e-mail",
        emailPlaceholder: "vous@exemple.org",
        organisation: "Organisation (facultatif)",
        organisationPlaceholder: "Votre organisation, le cas échéant",
        subject: "Sujet",
        subjectPlaceholder: "Objet de votre message",
        message: "Message",
        messagePlaceholder: "Votre message…",
        submit: "Envoyer le message",
        sending: "Envoi en cours…",
        successTitle: "Message envoyé",
        success: "Merci — votre message a bien été transmis. Nous vous répondrons à l'adresse indiquée.",
        errorTitle: "Échec de l'envoi",
        error: "Une erreur est survenue. Vous pouvez aussi nous écrire directement à",
        again: "Envoyer un autre message",
        errors: {
          name: "Veuillez indiquer votre nom (2 caractères minimum).",
          email: "Veuillez indiquer une adresse e-mail valide.",
          subject: "Veuillez indiquer un sujet.",
          message: "Votre message doit contenir au moins 10 caractères.",
        },
      },
      direct: {
        title: "En direct",
        emailLabel: "E-mail",
      },
      identity: {
        title: "Identité institutionnelle",
        line1: "Congo Bourse — mobilité internationale, innovation & technologie.",
        line2: "MouyIA — l'initiative Innovation & Technologie de Congo Bourse.",
        siteLabel: "Site institutionnel",
      },
      privacy: "Vos informations ne sont ni publiées ni cédées ; elles servent uniquement à vous répondre.",
      portraitAlt: "Portrait professionnel — Congo Bourse",
      portraitName: "Mouya Kadima Calixte Barthélemy",
      portraitRole: "Consultant Senior en Gouvernance SI, IA & Transformation Numérique",
    },
    footer: {
      tagline: "Relier les personnes, les opportunités et l'innovation.",
      navTitle: "Navigation",
      innovationTitle: "Innovation",
      learnova: "Learnova",
      mouyia: "MouyIA — Innovation & Technologie",
      applications: "Portfolio d'applications",
      contactTitle: "Contact",
      relationship: "MouyIA est l'initiative Innovation & Technologie de Congo Bourse.",
      rights: "Tous droits réservés.",
    },
    notFound: {
      title: "Page introuvable",
      text: "La page demandée n'existe pas ou a été déplacée.",
      cta: "Retour à l'accueil",
    },
  },

  en: {
    nav: {
      home: "Home",
      about: "About",
      innovation: "Innovation",
      applications: "Applications",
      mobility: "International Mobility",
      services: "Services",
      contact: "Contact",
      cta: "Discover Learnova",
      menuOpen: "Open menu",
      menuClose: "Close menu",
      langLabel: "Choose language",
      skip: "Skip to main content",
    },
    common: {
      status: {
        prototype: "Functional Prototype",
        development: "In Development",
        concept: "Concept / Roadmap",
      },
      learnMore: "Learn more",
      externalNewTab: "opens in a new tab",
    },
    home: {
      seo: {
        title: "Congo Bourse — Innovation & Technology",
        description:
          "Congo Bourse connects people, opportunities and innovation. Discover MouyIA, its Innovation & Technology initiative, and Learnova, a functional learning-intelligence prototype.",
      },
      hero: {
        eyebrow: "Congo Bourse — Innovation & Technology",
        lines: ["Connecting people,", "opportunities", "and innovation."],
        sub: "Congo Bourse combines experience in international mobility with a growing focus on digital innovation and technology-enabled solutions, serving educational and professional pathways.",
        ctaPrimary: "Explore our innovations",
        ctaSecondary: "Discover Learnova",
        frameAlt: "Professionals collaborating around digital technologies, AI and learning tools",
        frameCaption: "From the field to digital — the same rigour.",
        badgeTitle: "Learnova",
        badgeSub: "Functional prototype — public demonstration",
      },
      marquee: [
        "Congo Bourse",
        "MouyIA — Innovation & Technology",
        "Learnova — Functional Prototype",
        "From Goal to Demonstrable Skill",
        "International Mobility",
        "Digital Solutions",
      ],
      evolution: {
        eyebrow: "Our trajectory",
        title: "A coherent evolution",
        intro:
          "Congo Bourse has built its path step by step: from international mobility support to the design of digital solutions.",
        steps: [
          {
            title: "International Mobility",
            text: "Supporting educational and professional pathways beyond borders.",
          },
          {
            title: "Digital Transformation",
            text: "Embedding digital tools and technology-enabled services into our practice.",
          },
          {
            title: "Innovation & Technology",
            text: "Designing solutions for concrete problems: education, skills, mobility.",
          },
          {
            title: "MouyIA",
            text: "Congo Bourse's Innovation & Technology initiative, through which our digital solutions are developed and showcased.",
          },
          {
            title: "Digital Applications",
            text: "Concrete applications, each presented with its real stage of development.",
          },
        ],
      },
      featured: {
        eyebrow: "Featured innovation",
        name: "Learnova",
        category: "Goal-driven Learning Intelligence System",
        tagline: "From Goal to Demonstrable Skill.",
        description:
          "Learnova transforms what a learner wants to achieve into a structured learning pathway focused on skills development — rather than starting from a course catalogue.",
        ctaDiscover: "Discover Learnova",
        ctaPrototype: "View functional prototype",
        principles: [
          "Goal before Content",
          "Skill Intelligence",
          "Adaptive Learning",
          "Evidence before Completion",
          "Progress is not Mastery",
          "AI-assisted experience",
        ],
      },
      portfolio: {
        eyebrow: "Portfolio",
        title: "Our applications",
        text: "Every application is presented with its domain and its real maturity stage. Learnova is our flagship prototype today.",
        cta: "View the portfolio",
      },
      transparency: {
        eyebrow: "Transparency",
        title: "Verifiable innovation",
        points: [
          {
            title: "Real stages",
            text: "Every solution clearly displays its status: functional prototype, in development or concept.",
          },
          {
            title: "Public demonstrations",
            text: "Where prototypes and demos are accessible, they work without an account or prior approval.",
          },
          {
            title: "No exaggeration",
            text: "This site presents no customers, partners or figures that could not be verified.",
          },
        ],
      },
      ctaBand: {
        title: "Evaluators, partners, institutions",
        text: "Would you like to verify our approach or talk with us? Contact us directly.",
        cta: "Contact us",
      },
    },
    about: {
      seo: {
        title: "About — Congo Bourse",
        description:
          "The Congo Bourse journey: from international mobility and educational opportunities to digital innovation and technology.",
      },
      eyebrow: "About",
      title: "Our journey",
      intro:
        "Congo Bourse is an organisation rooted in international mobility and access to educational opportunities. Its story is told in four chapters.",
      chapters: [
        {
          num: "01",
          title: "Origins",
          text: "Congo Bourse was born from supporting international mobility and educational opportunities: helping people identify, prepare for and seize pathways beyond their immediate environment.",
        },
        {
          num: "02",
          title: "Evolution",
          text: "Through this field experience, digital tools and technology-enabled services gradually strengthened our practice — to better inform, support and follow each pathway.",
        },
        {
          num: "03",
          title: "Innovation",
          text: "This evolution opened a new stage: developing digital solutions addressing validated problems in education, skills and mobility.",
        },
        {
          num: "04",
          title: "Future",
          text: "Our ambition is the responsible use of artificial intelligence and digital technologies, to create practical solutions relevant to African contexts.",
        },
      ],
      imageAlt: "Professionals collaborating and learning around computers and digital tools",
      identity: {
        title: "Two identities, one organisation",
        cb: "Congo Bourse is the institutional identity: international mobility, services and support.",
        mouyia:
          "MouyIA is its Innovation & Technology initiative: the technology identity through which digital solutions are developed and showcased.",
      },
    },
    innovation: {
      seo: {
        title: "MouyIA — Innovation & Technology | Congo Bourse",
        description:
          "MouyIA is Congo Bourse's Innovation & Technology initiative: artificial intelligence, digital platforms and learning technologies serving practical solutions.",
      },
      eyebrow: "Innovation & Technology",
      title: "MouyIA",
      subtitle: "Congo Bourse's Innovation & Technology initiative",
      intro:
        "MouyIA is the initiative through which Congo Bourse explores, designs and showcases its digital solutions. It does not replace Congo Bourse: it extends its mission through technology.",
      exploreTitle: "What we explore",
      explore: [
        { title: "Artificial intelligence", text: "Targeted, responsible uses that serve human decision-making." },
        { title: "Digital platforms", text: "Accessible services designed for real-world contexts." },
        { title: "Automation", text: "Simplifying repetitive processes to free up human time." },
        { title: "Information systems", text: "Structuring information to decide and support better." },
        { title: "Data", text: "Using data with rigour and transparency." },
        { title: "Learning technologies", text: "Rethinking learning around demonstrable skills." },
      ],
      methodTitle: "Our method",
      method: [
        { title: "Problem", text: "Start from a real, observed and validated need." },
        { title: "Design", text: "Formalise a simple, useful answer." },
        { title: "Prototype", text: "Build a functional, testable version." },
        { title: "Validation", text: "Confront the prototype with real usage." },
        { title: "Improvement", text: "Iterate with rigour, without overselling." },
      ],
      aiNote:
        "Artificial intelligence is not presented as a replacement for human decision-making: it assists, informs and strengthens it.",
      flagship: {
        eyebrow: "Flagship application",
        text: "Learnova, the goal-driven learning intelligence system, is the first functional prototype to emerge from this approach.",
        cta: "Discover Learnova",
        ctaAll: "View all applications",
      },
    },
    applications: {
      seo: {
        title: "Applications — Congo Bourse",
        description:
          "The Congo Bourse and MouyIA applications portfolio: every solution is presented with its domain and its real maturity stage.",
      },
      eyebrow: "Portfolio",
      title: "Applications",
      intro:
        "Every application is published with its domain and its real maturity stage. No application is presented beyond its actual state.",
      legendTitle: "How to read the statuses",
      legend: [
        { status: "prototype", text: "The application works and can be demonstrated publicly." },
        { status: "development", text: "The application is being built; some features are not yet available." },
        { status: "concept", text: "The application is under study or planned; it is not yet usable." },
      ],
      flagshipLabel: "Flagship application",
      domainLabel: "Domain",
      domain: "Learning Intelligence Platform",
      learnovaDesc:
        "Learnova turns a learning goal into a structured, skills-focused pathway. The functional prototype can be demonstrated in French and English.",
      ctaPrototype: "View functional prototype",
      moreTitle: "An extensible portfolio",
      moreText:
        "Further approved applications will be published here over time, always with their real stage of development. No fictional product is presented.",
    },
    learnova: {
      seo: {
        title: "Learnova — Goal-driven Learning Intelligence System | Congo Bourse",
        description:
          "Learnova: From Goal to Demonstrable Skill. A publicly demonstrable functional prototype — goal definition, adaptive pathway, quizzes and progression.",
      },
      hero: {
        eyebrow: "Flagship application — MouyIA",
        name: "Learnova",
        category: "Goal-driven Learning Intelligence System",
        tagline: "From Goal to Demonstrable Skill.",
        description:
          "Learnova transforms what a learner wants to achieve into a structured learning pathway focused on skills development.",
        ctaPrototype: "View functional prototype",
        ctaDemo: "Watch the demo",
        maturityNote: "Current status: functional prototype. This is not a production-ready product.",
      },
      problem: {
        eyebrow: "The problem",
        title: "Platforms start with courses. Learners start with a goal.",
        text1:
          "Traditional learning platforms start with course catalogues: it is up to the learner to guess which ones will lead to their goal.",
        text2:
          "Yet a learner almost always starts with a different question: “What do I want to become capable of doing?”",
      },
      approach: {
        eyebrow: "The Learnova approach",
        title: "From goal to mastery",
        intro: "The Learnova model connects seven steps, from intention to demonstrable skill.",
        steps: [
          { label: "Goal", desc: "What the learner wants to become capable of doing." },
          { label: "Skills", desc: "Breaking the goal down into identified skills." },
          { label: "Learner state", desc: "The learner's real starting point." },
          { label: "Adaptive path", desc: "A structured learning path built from that state." },
          { label: "Learning", desc: "Learning activities guided by the path." },
          { label: "Evidence", desc: "Demonstrations of what has actually been acquired." },
          { label: "Mastery", desc: "Demonstrable skill — beyond mere progress." },
        ],
      },
      how: {
        eyebrow: "The current prototype",
        title: "How the prototype works today",
        intro:
          "This is the journey actually implemented in the current functional prototype, demonstrable in French and English.",
        steps: [
          "Goal definition",
          "Goal analysis",
          "Explicit human confirmation",
          "Learning roadmap",
          "Dashboard",
          "Learning activity",
          "Quiz assessment",
          "Progression",
        ],
      },
      maturity: {
        eyebrow: "Product transparency",
        title: "What exists — and what is under development",
        nowTitle: "Available now",
        now: [
          "Goal definition",
          "Goal analysis",
          "Explicit human confirmation",
          "Learning roadmap",
          "Dashboard",
          "Learning activity",
          "Quiz assessment",
          "Progression",
          "Goal-before-Path rule",
          "Initial domain foundation",
        ],
        roadmapTitle: "Product roadmap / under development",
        roadmap: [
          "Advanced Evidence Engine",
          "Mastery Engine",
          "Advanced adaptive intelligence",
          "Institutional multi-user capabilities",
        ],
        disclaimer:
          "Roadmap items are not current features. They indicate the product's development direction.",
      },
      evidence: {
        eyebrow: "Product evidence",
        title: "Functional Prototype — Real Screenshots",
        subtitle: "Real screenshots from the current Learnova functional prototype.",
        steps: [
          {
            title: "Goal definition",
            caption:
              "The learner defines the intended learning outcome, current level and available learning time.",
            alt: "Real screenshot of the Learnova prototype: goal definition screen with current level and available learning time",
          },
          {
            title: "Personalised learning roadmap",
            caption:
              "Learnova translates the learner's goal into a structured, personalised learning itinerary.",
            alt: "Real screenshot of the Learnova prototype: personalised six-step learning itinerary",
          },
          {
            title: "Learning activity & assessment",
            caption:
              "The learner follows a learning activity and completes a validation quiz within the generated pathway.",
            alt: "Real screenshot of the Learnova prototype: learning module with validation quiz",
          },
          {
            title: "Learner progression",
            caption:
              "The learner dashboard records progression through the personalised learning journey.",
            alt: "Real screenshot of the Learnova prototype: learner progression dashboard",
          },
          {
            title: "Prototype home",
            caption:
              "The current Learnova prototype introduces the Goal → AI → Personalised Path experience.",
            alt: "Real screenshot of the Learnova prototype: home page introducing the Goal, AI, Personalised Path experience",
          },
        ],
        enlarge: "Enlarge screenshot",
        close: "Close",
        cta: "Explore the live prototype",
        note: "The prototype is publicly accessible and demonstrates the current Goal → Analysis → Confirmation → Roadmap → Learning → Quiz → Progression journey.",
      },
      video: {
        eyebrow: "Video demonstration",
        title: "Watch Learnova in operation",
        note: "A real demonstration of the current functional prototype — presented in English.",
        iframeTitle: "Learnova prototype video demonstration",
      },
      live: {
        title: "Open the functional prototype",
        text: "The prototype is publicly accessible, with no account required, in a new tab — on desktop and mobile alike.",
        cta: "Open the prototype",
        urlLabel: "Prototype address",
      },
    },
    mobility: {
      seo: {
        title: "International Mobility — Congo Bourse",
        description:
          "Congo Bourse operates in international mobility and related support services, particularly in the field of educational opportunities.",
      },
      eyebrow: "Our founding activity",
      title: "International Mobility",
      intro:
        "Congo Bourse operates in international mobility and related support services, particularly in the field of educational opportunities. This activity is the organisation's foundation and the source of its understanding of learner pathways.",
      imageAlt: "Person holding a Congo Bourse “Études & Bourses assurées” banner in the street",
      bridgeTitle: "The link with innovation",
      bridge:
        "It is the experience of international mobility — its needs, its frictions, its pathways — that led Congo Bourse towards digital transformation and then towards the technological innovation carried by MouyIA. The two activities are distinct: this page concerns mobility only.",
      noteTitle: "Detailed information",
      note: "Detailed information about our mobility services is being validated and will be published on this page. For any question, contact us directly.",
      cta: "Contact us",
    },
    services: {
      seo: {
        title: "Services — Congo Bourse",
        description:
          "Congo Bourse provides services related to international mobility and related support.",
      },
      eyebrow: "Services",
      title: "Our services",
      intro:
        "Congo Bourse provides services related to international mobility and related support, alongside its technology innovation activity.",
      noteTitle: "Catalogue being published",
      note: "The details of our services are being validated and will be published here soon. This page is structured to be enriched without redesign. For any enquiry, contact us directly.",
      cta: "Contact us",
    },
    contact: {
      seo: {
        title: "Contact — Congo Bourse",
        description:
          "Contact Congo Bourse: evaluations, partnerships, international mobility or questions about our innovations.",
      },
      eyebrow: "Contact",
      title: "Write to us",
      intro:
        "Evaluators, partners, institutions or the simply curious: this form delivers your message directly to our team.",
      form: {
        name: "Full name",
        namePlaceholder: "Your full name",
        email: "Email address",
        emailPlaceholder: "you@example.org",
        organisation: "Organisation (optional)",
        organisationPlaceholder: "Your organisation, if applicable",
        subject: "Subject",
        subjectPlaceholder: "What your message is about",
        message: "Message",
        messagePlaceholder: "Your message…",
        submit: "Send the message",
        sending: "Sending…",
        successTitle: "Message sent",
        success: "Thank you — your message has been delivered. We will reply to the address you provided.",
        errorTitle: "Sending failed",
        error: "Something went wrong. You can also write to us directly at",
        again: "Send another message",
        errors: {
          name: "Please enter your name (at least 2 characters).",
          email: "Please enter a valid email address.",
          subject: "Please enter a subject.",
          message: "Your message must be at least 10 characters long.",
        },
      },
      direct: {
        title: "Direct contact",
        emailLabel: "Email",
      },
      identity: {
        title: "Institutional identity",
        line1: "Congo Bourse — international mobility, innovation & technology.",
        line2: "MouyIA — Congo Bourse's Innovation & Technology initiative.",
        siteLabel: "Institutional website",
      },
      privacy: "Your information is neither published nor shared; it is used only to reply to you.",
      portraitAlt: "Professional portrait — Congo Bourse",
      portraitName: "Mouya Kadima Calixte Barthélemy",
      portraitRole: "Senior Consultant in IT Governance, AI & Digital Transformation",
    },
    footer: {
      tagline: "Connecting people, opportunities and innovation.",
      navTitle: "Navigation",
      innovationTitle: "Innovation",
      learnova: "Learnova",
      mouyia: "MouyIA — Innovation & Technology",
      applications: "Applications portfolio",
      contactTitle: "Contact",
      relationship: "MouyIA is Congo Bourse's Innovation & Technology initiative.",
      rights: "All rights reserved.",
    },
    notFound: {
      title: "Page not found",
      text: "The page you requested does not exist or has moved.",
      cta: "Back to homepage",
    },
  },
};
